/**
 * @param {object} Drupal - The Drupal object.
 * @param {Function} once - The once function.
 */
(function (Drupal, once) {
  'use strict';

  /**
   * Handles the collapse animation.
   *
   * @param {HTMLElement} chapterList - The chapter list element to collapse.
   */
  function collapseSection(chapterList) {
    chapterList.style.height = chapterList.scrollHeight + 'px';
    // Force reflow
    void chapterList.offsetHeight;
    chapterList.classList.add('is-collapsing');
    chapterList.style.height = '0';

    setTimeout(() => {
      chapterList.classList.remove('is-collapsing');
      chapterList.classList.add('is-collapsed');
      chapterList.style.height = '';
    }, 300);
  }

  /**
   * Handles the expand animation.
   * @param {HTMLElement} chapterList - The chapter list element to expand.
   */
  function expandSection(chapterList) {
    chapterList.classList.remove('is-collapsed');
    chapterList.classList.add('is-collapsing');
    chapterList.style.height = '0';
    // Force reflow
    void chapterList.offsetHeight;
    chapterList.style.height = chapterList.scrollHeight + 'px';

    setTimeout(() => {
      chapterList.classList.remove('is-collapsing');
      chapterList.style.height = '';
    }, 300);
  }

  /**
   * Handles click event for section toggles.
   * @param {Event} e - The click event.
   */
  function handleToggleClick(e) {
    e.preventDefault();
    const sectionLink = this;
    const chapterList = sectionLink.parentElement.querySelector(':scope > ul');
    const isExpanded = sectionLink.getAttribute('aria-expanded') === 'true';
    sectionLink.setAttribute('aria-expanded', !isExpanded);

    if (isExpanded) {
      collapseSection(chapterList);
    }
    else {
      expandSection(chapterList);
    }
  }

  /**
   * Initializes the page navigator component.
   * @param {HTMLElement} pageNavigator - Page Navigator element Reference
   * @param {HTMLElement} pageSections - Page sections that act as anchor targets for Page Navigator
   */
  function initializeAnchorHighlights(pageNavigator, pageSections) {
    if (!pageNavigator || !pageSections.length) {
      return;
    }

    // Observe when page sections are scrolled into view to implementing anchor link highlighting
    const pageSectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const pageSection = entry.target;
        const anchorTarget = pageSection.querySelector('a');

        if (!anchorTarget) {
          return;
        }

        const anchorHref = `#${anchorTarget.id}`;
        const anchor = pageNavigator.querySelector(`[href="${anchorHref}"]`);

        // Highlight active anchor link when associated page section is scrolled into view
        if (entry.isIntersecting) {
          const previousAnchor = pageNavigator.querySelector('.page-navigator__link--active');

          // Deactivate previously active anchor link to ensure only one anchor link is highlighted
          if (previousAnchor) {
            previousAnchor.classList.remove('page-navigator__link--active');
          }

          anchor.classList.add('page-navigator__link--active');
        }
        else {
          const pageSectionPosY = entry.boundingClientRect.y;

          // Only deactivate if scrolled above the first or below the last page section
          const shouldDeactivate = entry.intersectionRect.bottom === 0
            && ((pageSections[0] === pageSection && pageSectionPosY > 0)
              || (pageSections[pageSections.length - 1] === pageSection && (pageSectionPosY < 0 && !entry.isIntersecting)));

          if (shouldDeactivate) {
            anchor.classList.remove('page-navigator__link--active');
          }
        }

      });
    }, {rootMargin: '-50% 0%'});

    pageSections.forEach((section) => {
      pageSectionObserver.observe(section);
    });
  }

  /**
   * Initializes a section with collapsible functionality.
   * @param {HTMLElement} section - The section element to initialize.
   * @param {number} index - The index of the section.
   */
  function initializeSection(section, index) {
    const sectionLink = section.querySelector(':scope > a');
    const chapterList = section.querySelector(':scope > ul');

    if (!sectionLink || !chapterList) {
      return;
    }

    // Create unique IDs for ARIA relationships
    const sectionId = `page-nav-section-${Date.now()}-${index}`;
    const chapterListId = `${sectionId}-chapters`;

    // Set ID on chapter list for ARIA reference
    chapterList.id = chapterListId;

    // Convert link to button-like behavior
    sectionLink.setAttribute('role', 'button');
    sectionLink.setAttribute('aria-expanded', 'false'); // Start collapsed
    sectionLink.setAttribute('aria-controls', chapterListId);
    sectionLink.classList.add('page-navigator__section-toggle');

    // Start with chapters collapsed
    chapterList.classList.add('page-navigator__chapters');
    chapterList.classList.add('is-collapsed');

    // Handle toggle functionality
    sectionLink.addEventListener('click', handleToggleClick);
  }

  /**
   * Initializes the page navigator component.
   * @param {HTMLElement} pageNavigator - The navigator element to initialize.
   * @param {HTMLElement} pageSections - Page sections that act as anchor targets for Page Navigator
   */
  function initializeNavigator(pageNavigator, pageSections) {
    const collapseToggle = pageNavigator.querySelector('.page-navigator__title');

    collapseToggle.addEventListener('click', () => {
      const links = pageNavigator.querySelector('.page-navigator__links');
      links.classList.toggle('is-collapsed');
      collapseToggle.setAttribute('aria-expanded', links.classList.contains('is-collapsed'));
    });

    // Create a new IntersectionObserver to check when a section is in view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        entry.target.classList.toggle('is-in-view', entry.intersectionRatio < 0.5);
      });
    }, {threshold: [1]});

    // Observe the section link to trigger the observer
    observer.observe(pageNavigator);

    initializeAnchorHighlights(pageNavigator, pageSections);

    // Skip collapsing for sections-only and chapters-only variants
    if (pageNavigator.classList.contains('page-navigator--sections-only') ||
      pageNavigator.classList.contains('page-navigator--chapters-only')) {
      return;
    }

    // Find all sections that have chapters
    const sectionsWithChapters = pageNavigator.querySelectorAll('.page-navigator__links > ul > li:has(ul)');
    sectionsWithChapters.forEach(initializeSection);
  }

  Drupal.behaviors.pageNavigator = {
    attach: function (context) {
      const navigators = once('page-navigator', '.page-navigator', context);
      let pageSections = once('page-sections', '.node--type-article .paragraph--type--section', context);

      if (!pageSections.length) {
        pageSections = once('page-sections', '.node--type-itinerary .chapter', context);
      }

      navigators.forEach((navigator) => {
        initializeNavigator(navigator, pageSections);
      });
    }
  };

})(Drupal, once);
